import React, { useEffect, useState } from 'react';
import { useSetRecoilState } from 'recoil';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useInterval, useUpdateEffect } from 'usehooks-ts';
import { TablePaginationConfig } from 'antd';

import './index.less';

import { alertsSelectorAdd } from '../../../../../recoil';
import {
  ContentStore,
  IngestionArtifactModel,
  IngestionArtifactType,
  IngestionProcessCreationRequest,
  IngestionResourceModel,
  IngestionSourceCommand,
  IngestionSourceParametersType,
  IngestionSourceModelOutput,
  ProjectModel,
} from '../../../../../../gamma-api';
import { defaultApi } from '../../../../../apis';
import { LARGE_PAGE_SIZE, RESOURCES_PAGE_SIZE } from '../../../../../constants';
import { getPageFromSearchParams, PAGE_INDEX_PARAM } from '../../../../../utils/urlUtil';
import { getProjectPath, getProjectSourcesPath, getProjectsPath } from '../../../../../utils/pathUtils';
import IbPageTitle, { IBreadcrumb } from '../../../../../components/IbPageTitle';
import IbConfirmModal from '../../../../../components/common/IbConfirmModal';
import IbButton from '../../../../../components/common/IbButton';
import IbTypography from '../../../../../components/common/IbTypography';
import IbDebounceInput from '../../../../../components/common/IbDebounceInput';
import IbIcon from '../../../../../components/common/IbIcon';
import ResourceTable, { ResourceTableField } from '../ResourceTable';
import DocumentList from '../DocumentList';
import ArticleList from '../ArticleList';
import FragmentList from '../FragmentList';
import SourceHeader from '../SourceHeader';
import SourceModal from '../SourceModal';

import { CONTENT_CLASS_NAME, MAIN_CLASS_NAME, REFRESH_INTERVAL, SEARCH_CLASS_NAME } from './const';

type PostFetchAction = {
  action: 'selectFirst' | 'selectLast';
  artifactType: IngestionArtifactType;
};

type ResourceTablePaginationConfig = TablePaginationConfig & {
  totalPageCount: number;
};

const SourcePage: React.FC = () => {
  const { t } = useTranslation();

  const location = useLocation();
  const { push } = useHistory();

  const { folderId, projectId, sourceId } = useParams<{ folderId: string; projectId: string; sourceId: string }>();

  const addAlert = useSetRecoilState(alertsSelectorAdd);

  const [sourceLoading, setSourceLoading] = useState(false);
  const [sourceUpdating, setSourceUpdating] = useState(false);
  const [sourceIngestion, setSourceIngestion] = useState(false);
  const [sourceDeletion, setSourceDeletion] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  const [projectItem, setProjectItem] = useState<ProjectModel>();
  const [sourceItem, setSourceItem] = useState<IngestionSourceModelOutput>();
  const [resourceItem, setResourceItem] = useState<IngestionResourceModel>();

  const [artifactMap, setArtifactMap] = useState<
    Record<IngestionArtifactType, IngestionArtifactModel[] | null | undefined>
  >({
    [IngestionArtifactType.Document]: undefined,
    [IngestionArtifactType.Article]: undefined,
    [IngestionArtifactType.Fragment]: undefined,
    [IngestionArtifactType.Embedding]: undefined,
  });

  const [resourceList, setResourceList] = useState<IngestionResourceModel[]>([]);
  const [resourceSearchText, setResourceSearchText] = useState('');
  const [resourcePagination, setResourcePagination] = useState<ResourceTablePaginationConfig>({
    current: getPageFromSearchParams(location.search),
    pageSize: RESOURCES_PAGE_SIZE,
    total: 0,
    totalPageCount: 0,
  });

  const [postFetchAction, setPostFetchAction] = useState<PostFetchAction | null>(null);

  const fetchProjectItem = async () => {
    try {
      const response = await defaultApi.getOneProjectGeneralProjectsProjectIdGet(projectId);
      setProjectItem(response.data);
    } catch (err) {
      addAlert({ content: t('Failed to load project'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchSourceItem = async () => {
    try {
      const response = await defaultApi.getOneSourceIngestionSourcesSourceIdGet(sourceId);
      setSourceItem(response.data);
    } catch (err) {
      addAlert({ content: t('Failed to load source'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchResourceList = async () => {
    try {
      const resourcesResponse = await defaultApi.searchResourcesIngestionResourcesGet(
        null,
        [sourceId],
        null,
        null,
        resourceSearchText,
        (resourcePagination.current || 1) - 1,
        RESOURCES_PAGE_SIZE,
        true
      );
      setResourceList(resourcesResponse.data.items || []);
      setResourcePagination((prev) => ({
        ...prev,
        total: resourcesResponse.data.total_item_count || 0,
        totalPageCount: resourcesResponse.data.total_page_count || 0,
      }));
    } catch (err) {
      addAlert({ content: t('Failed to load resources'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const fetchArtifactList = async (
    resourceId: string,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => {
    try {
      setArtifactMap({ ...artifactMap, [artifactType]: undefined, [oldArtifactType ?? '']: undefined });
      const listResponse = await defaultApi.searchArtifactsIngestionArtifactsGet(
        undefined,
        undefined,
        undefined,
        undefined,
        [resourceId],
        undefined,
        undefined,
        [artifactType],
        undefined,
        undefined,
        undefined,
        undefined,
        undefined,
        LARGE_PAGE_SIZE
      );
      for (const artifactItem of listResponse.data.items || []) {
        if (artifactItem.content.store === ContentStore.Document) {
          continue;
        }

        const itemResponse = await defaultApi.downloadArtifactStringIngestionArtifactsArtifactIdContentStringGet(
          artifactItem.artifact_id
        );
        artifactItem.content.data = itemResponse.data;
      }
      setArtifactMap({ ...artifactMap, [artifactType]: listResponse.data.items, [oldArtifactType ?? '']: undefined });
    } catch (err) {
      addAlert({ content: t('Failed to load artifact'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
    }
  };

  const onInitialLoadAsync = async () => {
    await fetchProjectItem();
    await fetchSourceItem();
    await fetchResourceList();
  };

  const onInitialLoadSync = () => {
    onInitialLoadAsync().finally();
  };

  useEffect(onInitialLoadSync, []);

  const onPageOrSearchChange = async () => {
    setPageLoading(true);
    await fetchResourceList();
    setPageLoading(false);
  };

  useEffect(() => {
    onPageOrSearchChange().finally();
  }, [resourcePagination.current, resourcePagination.pageSize, resourceSearchText]);

  const onRefreshLoadAsync = async () => {
    await fetchResourceList();
    await fetchSourceItem();
  };

  const onRefreshLoadSync = () => {
    onRefreshLoadAsync().finally();
  };

  useInterval(onRefreshLoadSync, REFRESH_INTERVAL);

  useEffect(() => {
    const page = getPageFromSearchParams(location.search);
    if (page !== resourcePagination.current) {
      setResourcePagination((prev) => ({ ...prev, current: page }));
    }
  }, [location.search]);

  useUpdateEffect(() => {
    if (resourcePagination.current !== 1) {
      setResourcePagination({ ...resourcePagination, current: 1 });
    }
  }, [resourceSearchText]);

  useUpdateEffect(() => {
    if (resourcePagination.current) {
      const params = new URLSearchParams(location.search);
      params.set(PAGE_INDEX_PARAM, resourcePagination.current.toString());
      push({ pathname: location.pathname, search: params.toString() });
    }
  }, [resourcePagination.current]);

  useUpdateEffect(() => {
    if (postFetchAction && resourceList.length > 0) {
      let resourceToSelect: IngestionResourceModel | undefined;

      if (postFetchAction.action === 'selectFirst') {
        resourceToSelect = resourceList[0];
      } else {
        resourceToSelect = resourceList[resourceList.length - 1];
      }

      if (resourceToSelect) {
        setResourceItem(resourceToSelect);
        fetchArtifactList(resourceToSelect.resource_id, postFetchAction.artifactType).finally();
      }

      setPostFetchAction(null);
    }
  }, [resourceList]);

  const onSourceSetup = () => {
    setSourceUpdating(true);
  };

  const onSourceClose = () => {
    setSourceUpdating(false);
  };

  const onSourceIngest = () => {
    setSourceIngestion(true);
  };

  const onSourceDelete = () => {
    setSourceDeletion(true);
  };

  const onProcessRestart = async () => {
    setSourceLoading(true);

    const processRequest: IngestionProcessCreationRequest = {
      project_id: projectId,
      brief_instructions: [
        {
          source_id: sourceId,
          command: IngestionSourceCommand.Restart,
          action: null,
          stages: {},
        },
      ],
    };

    try {
      await defaultApi.createProcessIngestionProcessesPost(processRequest);
    } catch (err) {
      addAlert({ content: t('Failed to restart source ingestion'), type: 'error' });
      // eslint-disable-next-line no-console
      console.error(err);
      setSourceLoading(false);
      return;
    }

    await fetchResourceList();
    await fetchSourceItem();

    setSourceLoading(false);
  };

  const onResourceOpen = (
    selectedResource: IngestionResourceModel,
    artifactType: IngestionArtifactType,
    oldArtifactType?: IngestionArtifactType
  ) => {
    setResourceItem(selectedResource);
    fetchArtifactList(selectedResource.resource_id, artifactType, oldArtifactType).finally();
  };

  const onResourceClose = (artifactType: IngestionArtifactType) => {
    setArtifactMap({ ...artifactMap, [artifactType]: undefined });
  };

  const onSourceItemChange = async () => {
    setPageLoading(true);
    await fetchSourceItem();
    setPageLoading(false);
  };

  const onResourceSearchTextChange = (value: string) => setResourceSearchText(value);

  const onResourcePaginationChange = (page: number) => setResourcePagination({ ...resourcePagination, current: page });

  const onResourceNext = (selectedResource: IngestionResourceModel, artifactType: IngestionArtifactType) => {
    const selectedIndex = resourceList.findIndex((resource) => resource.resource_id === selectedResource.resource_id);

    const isLastOnPage = selectedIndex >= resourceList.length - 1;
    const totalPages = resourcePagination.totalPageCount;
    const hasNextPage = (resourcePagination.current || 1) < totalPages;

    if (isLastOnPage) {
      if (hasNextPage) {
        setPostFetchAction({ action: 'selectFirst', artifactType });
        onResourcePaginationChange((resourcePagination.current || 1) + 1);
      } else {
        setPostFetchAction({ action: 'selectFirst', artifactType });
        onResourcePaginationChange(1);
      }
    } else {
      const nextIndex = selectedIndex + 1;
      const nextResource = resourceList[nextIndex];
      setResourceItem(nextResource);
      fetchArtifactList(nextResource.resource_id, artifactType).finally();
    }
  };

  const onResourcePrevious = (selectedResource: IngestionResourceModel, artifactType: IngestionArtifactType) => {
    const selectedIndex = resourceList.findIndex((resource) => resource.resource_id === selectedResource.resource_id);
    const totalPages = resourcePagination.totalPageCount;

    const isFirstOnPage = selectedIndex === 0;
    const hasPreviousPage = (resourcePagination.current || 1) > 1;

    if (isFirstOnPage) {
      if (hasPreviousPage) {
        setPostFetchAction({ action: 'selectLast', artifactType });
        onResourcePaginationChange((resourcePagination.current || 1) - 1);
      } else {
        setPostFetchAction({ action: 'selectLast', artifactType });
        onResourcePaginationChange(totalPages);
      }
    } else {
      const previousIndex = selectedIndex - 1;
      const previousResource = resourceList[previousIndex];

      setResourceItem(previousResource);
      fetchArtifactList(previousResource.resource_id, artifactType).finally();
    }
  };

  const renderResources = () => {
    const resourceItems = resourceList.map((resource) => ({ resource }));
    const resourceFields = [
      ResourceTableField.Name,
      ResourceTableField.Url,
      ResourceTableField.Volume,
      ResourceTableField.Status,
      ResourceTableField.Date,
      ResourceTableField.Context,
    ].filter(
      (field) =>
        field !== ResourceTableField.Url ||
        sourceItem?.versions.latest.source.parameters.type !== IngestionSourceParametersType.File
    );
    return (
      <ResourceTable
        fields={resourceFields}
        items={resourceItems}
        pageLoading={pageLoading}
        pagination={resourcePagination}
        searchText={resourceSearchText}
        sourceLoading={!sourceItem}
        onOpen={onResourceOpen}
        onPaginationChange={onResourcePaginationChange}
      />
    );
  };

  const renderDocuments = () => {
    const artifactList = artifactMap[IngestionArtifactType.Document];
    return (
      <DocumentList
        items={artifactList || []}
        loading={!artifactList}
        resource={resourceItem}
        visible={artifactList !== undefined}
        onClose={onResourceClose}
        onOpen={onResourceOpen}
      />
    );
  };

  const renderArticles = () => {
    const artifactList = artifactMap[IngestionArtifactType.Article];
    return (
      <ArticleList
        items={artifactList || []}
        loading={!artifactList}
        resource={resourceItem}
        visible={artifactList !== undefined}
        onClose={onResourceClose}
        onOpen={onResourceOpen}
        onResourceNext={onResourceNext}
        onResourcePrevious={onResourcePrevious}
      />
    );
  };

  const renderFragments = () => {
    const artifactList = artifactMap[IngestionArtifactType.Fragment];
    return (
      <FragmentList
        items={artifactList || []}
        loading={!artifactList}
        resource={resourceItem}
        visible={artifactList !== undefined}
        onClose={onResourceClose}
        onOpen={onResourceOpen}
        onResourceNext={onResourceNext}
        onResourcePrevious={onResourcePrevious}
      />
    );
  };

  const renderSourceModal = () => {
    return (
      <SourceModal
        key={sourceId}
        pipeline={projectItem?.pipeline.ingestion}
        projectId={projectId}
        sourceId={sourceId}
        sourceType={sourceItem?.versions.draft.source.parameters.type}
        visible={sourceUpdating}
        onChange={onSourceItemChange}
        onClose={onSourceClose}
      />
    );
  };

  const renderIngestionConfirmModal = () => {
    const onConfirmClick = async () => {
      setSourceLoading(true);

      const processRequest: IngestionProcessCreationRequest = {
        project_id: projectId,
        brief_instructions: [
          {
            source_id: sourceId,
            command: IngestionSourceCommand.Ingest,
            action: null,
            stages: {},
          },
        ],
      };

      try {
        await defaultApi.createProcessIngestionProcessesPost(processRequest);
      } catch (err) {
        addAlert({ content: t('Failed to start source ingestion'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setSourceLoading(false);
        return;
      }

      await fetchResourceList();
      await fetchSourceItem();

      setSourceLoading(false);
      setSourceIngestion(false);
    };

    const onCancelClick = () => {
      setSourceIngestion(false);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="warning" onClick={onConfirmClick}>
              {t('Ingest')}
            </IbButton>
            <IbButton key="delete-cancel" status="warning" type="secondary" onClick={onCancelClick}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={sourceLoading}
        title={t('Ingest source?')}
        visible={sourceIngestion}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Are you sure you want to ingest the source and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  const renderDeletionConfirmModal = () => {
    const onConfirmClick = async () => {
      setSourceLoading(true);

      const processRequest: IngestionProcessCreationRequest = {
        project_id: projectId,
        brief_instructions: [
          {
            source_id: sourceId,
            command: IngestionSourceCommand.Delete,
            action: null,
            stages: {},
          },
        ],
      };

      try {
        await defaultApi.createProcessIngestionProcessesPost(processRequest);
      } catch (err) {
        addAlert({ content: t('Failed to start source deletion'), type: 'error' });
        // eslint-disable-next-line no-console
        console.error(err);
        setSourceLoading(false);
        return;
      }

      await fetchResourceList();
      await fetchSourceItem();

      setSourceLoading(false);
      setSourceDeletion(false);
    };

    const onCancelClick = () => {
      setSourceDeletion(false);
    };

    return (
      <IbConfirmModal
        footer={
          <>
            <IbButton key="delete-confirm" status="error" onClick={onConfirmClick}>
              {t('Delete')}
            </IbButton>
            <IbButton key="delete-cancel" type="secondary" onClick={onCancelClick}>
              {t('Cancel')}
            </IbButton>
          </>
        }
        loading={sourceLoading}
        title={t('Delete source?')}
        visible={sourceDeletion}
        onCancel={onCancelClick}
      >
        <IbTypography.Paragraph type="secondary">
          {t('Are you sure you want to remove the source and its content?')}
        </IbTypography.Paragraph>
      </IbConfirmModal>
    );
  };

  const breadcrumbs: IBreadcrumb[] = [
    { title: t('Projects'), path: getProjectsPath(folderId) },
    { title: projectItem?.name ?? '', path: getProjectPath(folderId, projectItem?.project_id) },
    { title: t('Project tab: Sources'), path: getProjectSourcesPath(folderId, projectItem?.project_id) },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <IbPageTitle
        breadcrumbs={breadcrumbs}
        loading={(sourceLoading && !sourceUpdating) || !projectItem || !sourceItem}
        showSearchInput={false}
        title={sourceItem?.name}
      />
      <div className={CONTENT_CLASS_NAME}>
        <SourceHeader
          loading={sourceLoading}
          source={sourceItem}
          onDelete={onSourceDelete}
          onIngest={onSourceIngest}
          onRestart={onProcessRestart}
          onSetup={onSourceSetup}
        />
        <div className={SEARCH_CLASS_NAME}>
          <IbDebounceInput
            allowClear
            prefix={<IbIcon iconName="search" />}
            value={resourceSearchText}
            onDebouncedChange={onResourceSearchTextChange}
          />
        </div>
        {renderResources()}
      </div>
      {renderDocuments()}
      {renderArticles()}
      {renderFragments()}
      {renderSourceModal()}
      {renderIngestionConfirmModal()}
      {renderDeletionConfirmModal()}
    </div>
  );
};

export default SourcePage;
